import { Injectable, signal, computed } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import {
  InicializarMsalUseCase,
  RealizarLoginUseCase,
  ObterUsuarioUseCase,
  VerificarAutenticacaoUseCase
} from '../../../domain/auth/usecases';
import { AuthRepositoryImpl } from '../../../domain/auth/repositories/auth.repository.impl';
import {
  LoginRequest,
  UserModel,
  AuthState,
  initialAuthState,
  UserRole
} from '../../../domain/auth/models';
import { ModalService } from '../../../core/shared/components/modal/service/modal.service';
import { AUTHORIZATION_ROLE } from '../../../core/config/authorization-roles';
import { environment } from '../../../../environments/environment';

/**
 * ViewModel de autenticação seguindo padrão MVVM com Signals
 * Baseado no auth_view_model.dart do Flutter
 */
@Injectable({
  providedIn: 'root'
})
export class AuthViewModel {
  // Private signals - estado interno
  private readonly _state = signal<AuthState>(initialAuthState);
  private readonly _isInitialized = signal(false);
  private initializationPromise: Promise<boolean> | null = null;
  private sessionValidationPromise: Promise<boolean> | null = null;
  private sessionInvalidationPromise: Promise<void> | null = null;

  // Public readonly signals - estado exposto
  readonly state = this._state.asReadonly();
  readonly isInitialized = this._isInitialized.asReadonly();

  // Computed signals - estados derivados
  readonly isAuthenticated = computed(() => this._state().isAuthenticated);
  readonly isLoading = computed(() => this._state().isLoading);
  readonly user = computed(() => this._state().user);
  readonly error = computed(() => this._state().error);
  readonly hasError = computed(() => this._state().error !== null);
  readonly tokens = computed(() => this._state().tokens);

  // Computed signals para UI
  readonly canAccess = computed(() =>
    this.isAuthenticated() && !this.isLoading() && this.user() !== null
  );

  readonly hasBackofficeAccess = computed(() =>
    this.user()?.roles.includes(AUTHORIZATION_ROLE.BACKOFFICE_ACCESS) ?? false
  );

  readonly canAccessParameterization = computed(() =>
    (environment.skipAuth && !environment.production)
    || (this.user()?.roles.includes(AUTHORIZATION_ROLE.PARAMETERIZATION_ACCESS) ?? false)
  );

  readonly canAccessDocumentAnalysisLab = computed(() =>
    (environment.skipAuth && !environment.production)
    || (this.user()?.roles.includes(AUTHORIZATION_ROLE.DOCUMENT_ANALYSIS_LAB_ACCESS) ?? false)
    || (this.user()?.roles.includes(AUTHORIZATION_ROLE.BACKOFFICE_ACCESS) ?? false)
  );

  readonly userDisplayName = computed(() => {
    const user = this.user();
    return user?.profile?.displayName || user?.name || 'Usuário';
  });

  readonly userInitials = computed(() => {
    const user = this.user();
    if (!user?.profile) return 'U';

    const firstName = user.profile.firstName || '';
    const lastName = user.profile.lastName || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 'U';
  });

  readonly isAdmin = computed(() => this.hasRole(UserRole.ADMIN));
  readonly isGestor = computed(() => this.hasRole(UserRole.GESTOR));
  readonly isFuncionario = computed(() => this.hasRole(UserRole.FUNCIONARIO));

  constructor(
    private inicializarMsalUseCase: InicializarMsalUseCase,
    private realizarLoginUseCase: RealizarLoginUseCase,
    private obterUsuarioUseCase: ObterUsuarioUseCase,
    private verificarAutenticacaoUseCase: VerificarAutenticacaoUseCase,
    private authRepository: AuthRepositoryImpl,
    private router: Router,
    private modalService: ModalService
  ) {
    // Subscreve às mudanças de estado do repositório
    this.authRepository.authState$.subscribe(authState => {
      this._state.set(authState);
    });
  }

  /**
   * Inicializa o MSAL e verifica autenticação existente
   */
  async initialize(): Promise<boolean> {
    if (this._isInitialized()) {
      return true;
    }

    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    const initialization = this.initializeInternal();
    this.initializationPromise = initialization;

    try {
      return await initialization;
    } finally {
      if (this.initializationPromise === initialization) {
        this.initializationPromise = null;
      }
    }
  }

  private async initializeInternal(): Promise<boolean> {
    this._state.update(state => ({ ...state, isLoading: true, error: null }));

    try {


      const initialized = await firstValueFrom(
        this.inicializarMsalUseCase.execute()
      );

      if (initialized) {


        // Pequeno delay para garantir que o MSAL processou completamente
        await new Promise(resolve => setTimeout(resolve, 200));

        const isAuth = await firstValueFrom(
          this.verificarAutenticacaoUseCase.execute()
        );



        if (isAuth) {
          await this.loadUserData();
        } else {
          console.log('👋 Usuário não autenticado');

          // A sessão em cache pode ter sido rejeitada pelo backend antes de
          // qualquer componente protegido realizar uma requisição.
          this._state.update(state => ({
            ...state,
            isAuthenticated: false,
            isLoading: false,
            user: null,
            tokens: null
          }));

          // O authGuard retorna uma UrlTree e preserva a rota profunda
          // quando a navegação inicial for avaliada.
        }
      }

      this._state.update(state => ({ ...state, isLoading: false }));
      // Só libera os guards depois que a autenticação e o usuário foram
      // carregados. Antes disso, o roleGuard poderia observar user === null
      // e substituir a navegação profunda por /login ou /acesso-negado.
      this._isInitialized.set(true);

      return initialized;
    } catch (error) {
      console.error('❌ Erro na inicialização');
      this._state.update(state => ({
        ...state,
        isLoading: false,
        error: 'Erro ao inicializar autenticação'
      }));
      // Evita deixar o authGuard aguardando indefinidamente em caso de falha.
      this._isInitialized.set(true);
      return false;
    }
  }

  /**
   * Realiza login do usuário
   */
  async login(request: LoginRequest = {}): Promise<void> {
    this._state.update(state => ({ ...state, isLoading: true, error: null }));

    try {
      const tokens = await firstValueFrom(
        this.realizarLoginUseCase.execute(request)
      );

      await this.loadUserData();

      // Redirect baseado no role do usuário ou returnUrl
      this.router.navigateByUrl(this.getPostLoginRoute());

    } catch (error: any) {
      this._state.update(state => ({
        ...state,
        isLoading: false,
        error: this.getErrorMessage(error)
      }));
      throw error;
    }
  }

  /**
   * Valida a sessão atual usando aquisição silenciosa de token.
   * Útil quando a aba ficou aberta e o usuário retorna depois de horas.
   */
  async validateSession(): Promise<boolean> {
    if (this.sessionValidationPromise) {
      return this.sessionValidationPromise;
    }

    this.sessionValidationPromise = this.performSessionValidation();

    try {
      return await this.sessionValidationPromise;
    } finally {
      this.sessionValidationPromise = null;
    }
  }

  private async performSessionValidation(): Promise<boolean> {
    if (!this._isInitialized() || !this.isAuthenticated()) {
      return false;
    }

    try {
      const isValid = await firstValueFrom(this.authRepository.validateSession());
      if (isValid) {
        return true;
      }
    } catch {
      // Falha na renovação silenciosa significa que a sessão não é confiável.
    }

    await this.invalidateSession();
    return false;
  }

  /**
   * Encerra o estado autenticado e direciona imediatamente para o login.
   * Este método é compartilhado pela validação periódica e pelo interceptor
   * HTTP para impedir que uma rota protegida permaneça visível sem sessão.
   */
  async invalidateSession(): Promise<void> {
    if (this.sessionInvalidationPromise) {
      return this.sessionInvalidationPromise;
    }

    const returnUrl = this.router.url;
    this.sessionInvalidationPromise = (async () => {
      await firstValueFrom(this.authRepository.clearSession());

      if (!this.router.url.startsWith('/login')) {
        await this.router.navigate(['/login'], {
          queryParams: { returnUrl }
        });
      }
    })();

    try {
      await this.sessionInvalidationPromise;
    } finally {
      this.sessionInvalidationPromise = null;
    }
  }

  /**
   * Realiza logout do usuário
   */
  async logout(): Promise<void> {
    const confirmed = await firstValueFrom(
      this.modalService.confirm({
        title: 'Confirmar logout',
        message: 'Você será redirecionado para a autenticação da Microsoft. Deseja continuar?',
        confirmText: 'Confirmar'
      })
    );

    if (!confirmed) {
      return;
    }

    this._state.update(state => ({ ...state, isLoading: true }));

    try {
      await firstValueFrom(this.authRepository.logout());
      this.router.navigate(['/login']);
    } catch (error) {
      this._state.update(state => ({
        ...state,
        isLoading: false,
        error: 'Erro ao fazer logout'
      }));
    }
  }

  /**
   * Limpa erro do estado
   */
  clearError(): void {
    this._state.update(state => ({ ...state, error: null }));
  }

  /**
   * Verifica se o usuário tem uma role específica
   */
  hasRole(role: string): boolean {
    const user = this.user();
    return user?.roles.includes(role) ?? false;
  }

  /**
   * Verifica se o usuário tem qualquer uma das roles especificadas
   */
  hasAnyRole(roles: string[]): boolean {
    const user = this.user();
    if (!user) return false;
    return roles.some(role => user.roles.includes(role));
  }

  /**
   * Verifica se o usuário tem todas as roles especificadas
   */
  hasAllRoles(roles: string[]): boolean {
    const user = this.user();
    if (!user) return false;
    return roles.every(role => user.roles.includes(role));
  }

  /**
   * Verifica se o usuário tem uma permissão específica
   */
  hasPermission(permission: string): boolean {
    const user = this.user();
    return user?.permissions.includes(permission) ?? false;
  }

  /**
   * Verifica se o usuário tem qualquer uma das permissões especificadas
   */
  hasAnyPermission(permissions: string[]): boolean {
    const user = this.user();
    if (!user) return false;
    return permissions.some(permission => user.permissions.includes(permission));
  }

  /**
   * Obtém URL de retorno após login
   */
  getReturnUrl(): string {

    const params = new URLSearchParams(window.location.search);
    const returnUrl = params.get('returnUrl');


    if (returnUrl) {
      return returnUrl;
    }

    // Redirect baseado no role do usuário
    const user = this.user();


    if (user) {
      if (this.hasRole(UserRole.ADMIN) || this.hasRole(UserRole.GESTOR)) {
        return '/parametros'; // Temporário até criar rota de backoffice
      } else if (this.hasRole(UserRole.FUNCIONARIO)) {
        return '/dashboard';
      }
    }

    return '/dashboard';
  }

  /**
   * Durante o primeiro carregamento, a URL confirmada do Router ainda pode ser
   * `/` enquanto o authGuard aguarda a inicialização do MSAL. Nesse intervalo,
   * a navegação pendente contém a rota profunda acessada pelo usuário (por
   * exemplo, após pressionar F5 na consulta de um participante).
   */
  private getCurrentOrPendingUrl(): string {
    const navigation = this.router.getCurrentNavigation();
    const pendingUrl = navigation?.finalUrl?.toString()
      ?? navigation?.extractedUrl.toString();

    if (pendingUrl) {
      return pendingUrl;
    }

    if (this.router.url !== '/') {
      return this.router.url;
    }

    const browserUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;

    return browserUrl || this.router.url;
  }

  private isBrowserAtRootOrLogin(): boolean {
    const path = window.location.pathname;
    return path === '/' || path === '/login';
  }

  private getPostLoginRoute(): string {
    return this.hasBackofficeAccess() ? this.getReturnUrl() : '/acesso-negado';
  }

  /**
   * Carrega dados do usuário
   */
  private async loadUserData(): Promise<void> {
    try {
      const user = await firstValueFrom(this.obterUsuarioUseCase.execute());
      this._state.update(state => ({
        ...state,
        user,
        isAuthenticated: true,
        lastActivity: new Date()
      }));
    } catch (error) {
      console.error('Erro ao carregar dados do usuário');
    }
  }

  /**
   * Converte erro em mensagem amigável
   */
  private getErrorMessage(error: any): string {
    if (error?.message) {
      return error.message;
    }

    if (error?.errorCode) {
      switch (error.errorCode) {
        case 'user_cancelled':
          return 'Login cancelado pelo usuário';
        case 'consent_required':
          return 'Consentimento necessário para acessar o sistema';
        case 'interaction_required':
          return 'É necessário refazer o login';
        case 'popup_blocked':
          return 'Popup bloqueado pelo navegador. Permita popups para este site.';
        case 'network_error':
          return 'Erro de conexão. Verifique sua internet.';
        default:
          return 'Erro de autenticação. Tente novamente.';
      }
    }

    return 'Erro desconhecido. Tente novamente.';
  }
}
